import { Component, OnInit } from '@angular/core';
import { EstablishmentService } from '../../establishment.service';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { EquipmentModel } from '../../../../../../core/models/equipment.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EstablishmentActivityCreateUpdateDeleteComponent } from '../../modals/establishment-activity-create-update-delete/establishment-activity-create-update-delete.component';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { ActivityModel } from '../../../../../../core/models/activity.model';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { AddressUtils } from '../../../../../../core/utils/address-utils';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { ActivatedRoute } from '@angular/router';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { AuthService } from '../../../../../../core/services/auth.service';
import { Subscription } from 'rxjs';
import { EstablishmentTypeEnum } from '../../../../../../core/enums/establishment-type.enum';

@Component({
  selector: 'vex-establishment-details-activite',
  templateUrl: './establishment-details-activite.component.html',
  styleUrls: ['./establishment-details-activite.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class EstablishmentDetailsActiviteComponent implements OnInit {
  displayedColumns = ['actions'];
  count = 0;
  dataSource = new MatTableDataSource<ActivityModel>();
  sortField: string;
  sortOrder: string;
  pageIndex: number;
  pageSizeAddress = 0;
  canAddActivityPermissions = false;
  subscription: Subscription;
  establishmentType: EstablishmentTypeEnum;

  // Colonnes complètes pour établissement de type CARE (prise en charge)
  private fullFeisColumns: Array<FeiColumn> = [
    { label: 'Libellé', column: 'label', propertyDisplay: null },
    { label: "Type d'activité", column: 'activityType', propertyDisplay: 'activityType.label' },
    { label: 'Typologie des résidents', column: 'publicType', propertyDisplay: 'publicType.label' },
    { label: 'Pourcentage', column: 'percent', propertyDisplay: null, additionalDisplay: ' %' },
    { label: 'N° Finess', column: 'finess', propertyDisplay: null },
    { label: 'Adresses', column: 'formattedAddresses', propertyDisplay: null },
    { label: 'Nombre de lits permanents', column: 'nbOfPermanantBed' },
    { label: 'Nombre de lits temporaires', column: 'nbOfTemporaryBed' },
    { label: "Capacité d'accueil", column: 'accomodationCapacity' },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
      canArchive: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
    },
  ];

  // Colonnes réduites pour établissement de type SUPPORT_FUNCTION (fonction support)
  private supportFeisColumns: Array<FeiColumn> = [
    { label: 'Libellé', column: 'label', propertyDisplay: null },
    { label: "Type d'activité", column: 'activityType', propertyDisplay: 'activityType.label' },
    { label: 'Pourcentage', column: 'percent', propertyDisplay: null, additionalDisplay: ' %' },
    { label: 'Adresses', column: 'formattedAddresses', propertyDisplay: null },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
      canArchive: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
    },
  ];

  feisColumns: Array<FeiColumn> = [];

  loading = false;
  entityName = 'Activités';
  displayPostePercentWarning = false;
  ENUM_PERMISSION = ENUM_PERMISSIONS;

  constructor(
    public service: EstablishmentService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    public authService: AuthService,
  ) {
    // Les colonnes seront initialisées dans ngOnInit après avoir récupéré le type d'établissement
  }

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    this.loading = true;
    this.service.entity$.subscribe(establishment => {
      // Initialiser les colonnes selon le type d'établissement
      if (establishment?.establishmentType) {
        this.establishmentType = establishment.establishmentType;
        this.initializeColumnsForEstablishmentType();
      }

      let paginatedActivity = establishment?.activities || [];

      // Add sorting logic here
      if (this.sortField && this.sortOrder) {
        paginatedActivity.sort((a, b) => {
          let result = 0;
          if (this.sortOrder === 'asc') {
            result = a[this.sortField] > b[this.sortField] ? 1 : -1;
          } else {
            result = a[this.sortField] < b[this.sortField] ? 1 : -1;
          }
          return result;
        });
      }

      // Add pagination logic here
      if (this.pageIndex && this.pageSizeAddress) {
        paginatedActivity = paginatedActivity.slice(
          (this.pageIndex - 1) * this.pageSizeAddress,
          this.pageIndex * this.pageSizeAddress,
        );
      }

      // Enrichir les données avec les adresses formatées
      paginatedActivity = paginatedActivity.map(activity => ({
        ...activity,
        formattedAddresses: this.formatAddresses(activity.addresses),
      }));

      this.dataSource = new MatTableDataSource<ActivityModel>(paginatedActivity);
      this.count = establishment?.activities?.length;
      this.displayPostePercentWarning = establishment?.activities.reduce((p, c) => p + c.percent, 0) !== 100;
      this.loading = false;
    });
  }

  /**
   * Initialise les colonnes du tableau selon le type d'établissement
   */
  private initializeColumnsForEstablishmentType(): void {
    if (this.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
      // Pour les établissements de type fonction support : Libellé, Type d'activité, Pourcentage et Adresse
      this.feisColumns = [...this.supportFeisColumns];
    } else {
      // Pour les établissements de type prise en charge : toutes les colonnes
      this.feisColumns = [...this.fullFeisColumns];
    }
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  pageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex + 1;
    this.pageSizeAddress = event.pageSize;
    this.initData();
  }

  sortEvent(event: any) {
    console.log(event);
    this.sortField = event.active;
    this.sortOrder = event.direction;
    this.initData();
  }

  async createActivity() {
    this.dialog
      .open(EstablishmentActivityCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Create,
          establishment: await this.service.entity,
        },
      })
      .afterClosed()
      .subscribe((establishment: EquipmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  async updateItem(e) {
    this.dialog
      .open(EstablishmentActivityCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Update,
          defaults: e,
          establishment: await this.service.entity,
        },
      })
      .afterClosed()
      .subscribe((establishment: EquipmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  async deleteItem(e) {
    this.dialog
      .open(EstablishmentActivityCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Delete,
          establishment: await this.service.entity,
          defaults: e,
          name: e.label,
        },
      })
      .afterClosed()
      .subscribe((establishment: EquipmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  async archiveItem(e) {
    this.dialog
      .open(EstablishmentActivityCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Archive,
          establishment: await this.service.entity,
          defaults: e,
          name: e.label,
        },
      })
      .afterClosed()
      .subscribe((establishment: EquipmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  /**
   * Formate l'affichage des adresses avec leurs surfaces
   */
  formatAddresses(addresses: any[]): string {
    if (!addresses || addresses.length === 0) {
      return '-';
    }

    return addresses
      .map(addr => {
        const label = AddressUtils.formatInline(addr.addressData) || addr.addressData?.label || addr.addressId;
        const surface = addr.surface ? ` (${addr.surface.toFixed(2)} m²)` : '';
        return `${label}${surface}`;
      })
      .join(', ');
  }
}
